import React, { useRef, useState, useEffect, useMemo } from 'react';
import { parseUtc } from '../utils/time';
import {
  Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Box, Button, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Tooltip, TableSortLabel,
  IconButton, Menu, Checkbox, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, CircularProgress, Skeleton,
  useMediaQuery,
} from '@mui/material';
import GppBadIcon from '@mui/icons-material/GppBad';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  getAlerts, exportAlertsCsv, bulkAlertStatus,
  getAllowlist, addAllowlistEntry, deleteAllowlistEntry,
} from '../services/api';

/* ── constants ───────────────────────────────────────────── */

const CLASSIFICATIONS = [
  { value: 'active',        display: 'Active',        bg: 'rgba(248,113,113,0.18)', color: '#f87171' },
  { value: 'for_sale',     display: 'For Sale',      bg: 'rgba(251,146,60,0.15)', color: '#fb923c' },
  { value: 'redirect',     display: 'Redirect',      bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
  { value: 'parked',       display: 'Parked',        bg: 'rgba(255,255,255,0.08)', color: 'rgba(232,228,248,0.55)' },
  { value: 'unclassified', display: 'Unclassified',  bg: 'rgba(251,191,36,0.12)', color: '#fbbf24' },
  { value: 'inactive',     display: 'Inactive',      bg: 'rgba(74,222,128,0.12)', color: '#4ade80' },
];

const RISK = [
  { min: 90, label: 'CRITICAL', bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
  { min: 70, label: 'HIGH',     bg: 'rgba(251,146,60,0.15)',  color: '#fb923c' },
  { min: 40, label: 'MEDIUM',   bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },   // server tier boundary
  { min: 0,  label: 'LOW',      bg: 'rgba(74,222,128,0.12)',  color: '#4ade80' },
];

function getRisk(score) {
  return RISK.find((r) => score >= r.min) || RISK[RISK.length - 1];
}

const STATUS_META = {
  new:            { label: 'New',            color: 'rgba(232,228,248,0.55)', bg: 'rgba(255,255,255,0.08)' },
  reviewed:       { label: 'Reviewed',       color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  false_positive: { label: 'False positive', color: '#6b7280', bg: 'rgba(107,114,128,0.18)' },
};

// Detection source (matches the Overview's scan/NRD/stream/hunt language)
const SOURCE = {
  scan:   { label: 'Scan',      color: '#a78bfa' },
  nrd:    { label: 'NRD',       color: '#2dd4bf' },
  stream: { label: 'CT stream', color: '#4ade80' },
  feed:   { label: 'Feed',      color: '#fbbf24' },
  hunt:   { label: 'urlscan',   color: '#60a5fa' },
};
function detectionSource(ft) {
  ft = ft || '';
  if (ft.startsWith('nrd-')) return SOURCE.nrd;
  if (ft.startsWith('ctstream-')) return SOURCE.stream;
  if (ft.startsWith('feed-')) return SOURCE.feed;
  if (ft.startsWith('favicon-') || ft.startsWith('infra-')) return SOURCE.hunt;
  return SOURCE.scan;
}

// Prominent, scannable risk score: bold colored number + band + proportional bar
function ScoreBadge({ score }) {
  // Band from the raw score (as the filters and server counts do); floor the
  // number so it never shows a threshold the score has not reached
  const raw = score || 0;
  const s = Math.floor(raw);
  const risk = getRisk(raw);
  return (
    <Box sx={{ minWidth: 54, cursor: 'default' }}>
      <Box display="flex" alignItems="baseline" gap={0.5}>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1,
                          color: risk.color, letterSpacing: -0.5,
                          fontVariantNumeric: 'tabular-nums' }}>
          {s}
        </Typography>
        <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, letterSpacing: 0.5,
                          color: risk.color, opacity: 0.8 }}>
          {risk.label}
        </Typography>
      </Box>
      <Box sx={{ mt: 0.5, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.max(3, Math.min(100, s))}%`, height: '100%',
                   bgcolor: risk.color, borderRadius: 2 }} />
      </Box>
    </Box>
  );
}

/* ── detection type descriptions ──────────────────────────── */

const FUZZER_DESCRIPTIONS = {
  // urlscan search hunters
  'favicon-clone':    'Serves your brand\'s exact favicon (found via urlscan favicon-hash search): visual clone regardless of domain name',
  'infra-sibling':    'Hosted on the same IP as a confirmed high-risk lookalike (found via urlscan infra pivot): likely same campaign',
  // CT monitor classifications
  'subdomain':        'Subdomain of your domain found in certificate logs',
  'tld_variation':    'Your brand registered under a different TLD (.net, .org, etc.)',
  'subdomain_abuse':  'Your domain used as a subdomain on an attacker-controlled domain',
  'associated':       'Domain containing your brand name found in CT logs',
  'hyphenation':      'Hyphenated version of your brand (e.g. care-wise-health)',
  // dnstwist types
  'dnstwist-homoglyph':     'Look-alike characters substituted (e.g. rn instead of m)',
  'dnstwist-bitsquatting':  'Single bit-flip in the domain name (hardware error typo)',
  'dnstwist-cyrillic':      'Cyrillic characters replacing Latin letters',
  'dnstwist-omission':      'One character removed from the domain',
  'dnstwist-repetition':    'A character doubled in the domain (e.g. gooogle)',
  'dnstwist-replacement':   'Adjacent keyboard key substitution',
  'dnstwist-transposition': 'Two adjacent characters swapped',
  'dnstwist-insertion':     'Extra character inserted into the domain',
  'dnstwist-addition':      'Character appended to the domain',
  'dnstwist-vowel-swap':    'Vowels swapped (e.g. a to e)',
  'dnstwist-hyphenation':   'Hyphens inserted at word boundaries',
  'dnstwist-subdomain':     'Domain split into a fake subdomain (c.ertilytics)',
  'dnstwist-tld-swap':      'TLD changed to a different extension',
  'dnstwist-plural':        'Plural or singular variation',
  'dnstwist-dictionary':    'Common word prepended or appended',
  'dnstwist-various':       'Other character-level permutation',
  // Our own generator types
  'typosquatting':    'Keyboard-proximity typo or character omission/swap',
  'homoglyph':        'Unicode look-alike characters (e.g. Cyrillic "a" for Latin "a")',
  // Keyword combos
  'keyword-auth':       'Brand + authentication keyword (login, signin, auth)',
  'keyword-billing':    'Brand + billing keyword (pay, invoice, billing)',
  'keyword-security':   'Brand + security keyword (secure, verify, confirm)',
  'keyword-phishing':   'Brand + phishing keyword (alert, urgent, action)',
  'keyword-support':    'Brand + support keyword (help, support, desk)',
  'keyword-mail':       'Brand + email keyword (mail, webmail, email)',
  'keyword-portal':     'Brand + portal keyword (portal, dashboard, my)',
  'keyword-account':    'Brand + account keyword (account, profile, user)',
  'keyword-update':     'Brand + update keyword (update, renew, upgrade)',
  'keyword-corporate':  'Brand + corporate keyword (corp, inc, group)',
  'keyword-web':        'Brand + web keyword (online, web, site)',
  'keyword-healthcare': 'Brand + healthcare keyword (patient, claims, hipaa)',
  'keyword-payer-portal': 'Brand + payer/provider portal keyword (claimsportal, memberportal, priorauth)',
  'keyword-benefits': 'Brand + benefits/enrollment keyword (openenrollment, mybenefits, hsa)',
  'keyword-compliance': 'Brand + HIPAA/compliance lure keyword (breach, audit, hipaanotice)',
  'keyword-analytics': 'Brand + analytics/data keyword (insights, predictive, dataportal)',
  'keyword-regulatory': 'Brand + regulatory/government keyword (hipaa, cms, hitrust, oig)',
  'keyword-ai-healthtech': 'Brand + AI/healthtech keyword (genai, copilot, healthai, llm)',
  'keyword-product-brand': 'Brand + product name keyword (certhlm, benefitiq, fusionbi)',
  'keyword-payment-eft': 'Brand + EFT/payment keyword (eft, remittance, paymentportal)',
  'keyword-breach-lure': 'Brand + breach notification lure (breachnotice, dataincident, phidata)',
  // RDAP registration monitoring
  'rdap-exact-tld':        'Exact brand name registered on a different TLD (via RDAP)',
  'rdap-new-registration': 'Recently registered brand permutation detected via RDAP (may not be live yet)',
};

function getFuzzerDescription(type) {
  if (!type) return '';
  if (FUZZER_DESCRIPTIONS[type]) return FUZZER_DESCRIPTIONS[type];
  // Fallback for unknown types
  const clean = type.replace('dnstwist-', '').replace('keyword-', 'keyword ').replace(/_/g, ' ').replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\b\w+/g, w => ACRONYMS[w.toLowerCase()] || w);
  return `Detection type: ${clean}`;
}

const RDAP_STATUS_INFO = {
  'active':                       { label: 'Active',                   desc: 'Domain is registered and active' },
  'inactive':                     { label: 'Inactive',                 desc: 'Domain is registered but not active' },
  'client transfer prohibited':   { label: 'Transfer Locked',          desc: 'Registrar has locked transfers (common security measure)' },
  'client hold':                  { label: 'Client Hold',              desc: 'Registrar has placed a hold, domain may not resolve' },
  'client delete prohibited':     { label: 'Delete Protected',         desc: 'Registrar prevents accidental deletion' },
  'client renew prohibited':      { label: 'Renew Prohibited',         desc: 'Domain cannot be renewed by registrant' },
  'client update prohibited':     { label: 'Update Locked',            desc: 'Domain records cannot be modified' },
  'server transfer prohibited':   { label: 'Registry Transfer Lock',   desc: 'Registry-level transfer restriction' },
  'server hold':                  { label: 'Registry Hold',            desc: 'Registry has placed a hold, domain will not resolve' },
  'server delete prohibited':     { label: 'Registry Delete Lock',     desc: 'Registry prevents deletion' },
  'server update prohibited':     { label: 'Registry Update Lock',     desc: 'Registry prevents record changes' },
  'server renew prohibited':      { label: 'Registry Renew Lock',      desc: 'Registry prevents renewal' },
  'pending delete':               { label: 'Pending Delete',           desc: 'Domain is in deletion grace period' },
  'pending transfer':             { label: 'Pending Transfer',         desc: 'Domain transfer is in progress' },
  'pending create':               { label: 'Pending Create',           desc: 'Domain registration is being processed' },
  'pending renew':                { label: 'Pending Renew',            desc: 'Domain renewal is being processed' },
  'pending update':               { label: 'Pending Update',           desc: 'Domain update is being processed' },
  'redemption period':            { label: 'Redemption Period',        desc: 'Domain recently expired, can still be recovered' },
  'add period':                   { label: 'Add Period',               desc: 'Recently registered, in initial grace period' },
  'auto renew period':            { label: 'Auto-Renew Period',        desc: 'Domain was auto-renewed, in grace period' },
  'renew period':                 { label: 'Renew Period',             desc: 'Domain was renewed, in grace period' },
  'transfer period':              { label: 'Transfer Period',          desc: 'Domain was recently transferred' },
};

function getRdapStatusInfo(raw) {
  const key = raw.trim().toLowerCase();
  // Try exact match first (space-separated format from DB)
  if (RDAP_STATUS_INFO[key]) return RDAP_STATUS_INFO[key];
  // Try without spaces (camelCase collapsed)
  const noSpaces = key.replace(/\s+/g, '');
  for (const [k, v] of Object.entries(RDAP_STATUS_INFO)) {
    if (k.replace(/\s+/g, '') === noSpaces) return v;
  }
  // Fallback: title case the raw value
  const label = raw.trim().replace(/\b\w/g, c => c.toUpperCase());
  return { label, desc: `RDAP status: ${raw.trim()}` };
}

/* ── sub-components ──────────────────────────────────────── */

const BREAKDOWN_LABELS = {
  similarity: 'Similarity',
  intent: 'Intent',
  classification: 'Classification',
  mx_enabled: 'MX Enabled',
  cert_age: 'Cert Age',
  cert_issuer: 'Cert Issuer',
  dns_only: 'DNS-Only',
  threat_intel: 'Threat Intel',
  spamhaus_dbl: 'Spamhaus DBL',
  age: 'Domain Age',
  content_match: 'Content Match',
  popularity: 'Popularity',
  tld_risk: 'TLD Risk',
  corroboration: 'Corroboration',
  tier_adjustment: 'Evidence Gate',
  // Legacy fields for older scan results
  fuzzer_type: 'Fuzzer Type',
  active_boost: 'Active Boost',
};

function parseBreakdown(breakdown) {
  if (!breakdown) return [];
  try {
    const b = typeof breakdown === 'string' ? JSON.parse(breakdown) : breakdown;
    return Object.keys(BREAKDOWN_LABELS)
      .filter(k => typeof b[k] === 'number' && b[k] !== 0)
      .map(k => ({ label: BREAKDOWN_LABELS[k], pts: b[k] }));
  } catch { return []; }
}

function RiskBreakdown({ breakdown }) {
  const rows = parseBreakdown(breakdown);
  if (!rows.length) return null;
  return (
    <Box sx={{ p: 0.5, minWidth: 140 }}>
      {rows.map(r => (
        <Box key={r.label} display="flex" justifyContent="space-between" gap={2}
             sx={{ fontSize: '0.72rem', py: 0.2 }}>
          <span style={{ color: 'rgba(232,228,248,0.7)' }}>{r.label}</span>
          <span style={{ fontWeight: 700, color: r.pts < 0 ? '#4ade80' : '#e8e4f8' }}>
            {r.pts > 0 ? `+${r.pts}` : r.pts}
          </span>
        </Box>
      ))}
    </Box>
  );
}

/* Horizontal factor bars for the expanded detail panel */
function FactorBars({ breakdown }) {
  const rows = parseBreakdown(breakdown);
  if (!rows.length) {
    return <Typography sx={{ fontSize: '0.7rem', color: 'rgba(232,228,248,0.25)' }}>
      No breakdown recorded
    </Typography>;
  }
  const maxAbs = Math.max(...rows.map(r => Math.abs(r.pts)), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
      {rows.map(r => (
        <Box key={r.label}>
          <Box display="flex" justifyContent="space-between" mb={0.2}>
            <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.55)' }}>
              {r.label}
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700,
                              color: r.pts < 0 ? '#4ade80' : '#e8e4f8',
                              fontVariantNumeric: 'tabular-nums' }}>
              {r.pts > 0 ? `+${r.pts}` : r.pts}
            </Typography>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <Box sx={{
              width: `${(Math.abs(r.pts) / maxAbs) * 100}%`, height: '100%', borderRadius: 2,
              bgcolor: r.pts < 0 ? 'rgba(74,222,128,0.7)' : 'rgba(200,84,72,0.8)',
            }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const ACRONYMS = { tld: 'TLD', rdap: 'RDAP', ct: 'CT', mx: 'MX', spf: 'SPF', dmarc: 'DMARC', idn: 'IDN', ip: 'IP', dns: 'DNS', http: 'HTTP', https: 'HTTPS', ssl: 'SSL', bec: 'BEC' };
function fmtFuzzer(raw) {
  return raw.replace('dnstwist-', '').replace('keyword-', 'Keyword ').replace(/_/g, ' ').replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\b\w+/g, w => ACRONYMS[w.toLowerCase()] || w);
}

function FuzzerChip({ type }) {
  if (!type) return (
    <Chip label="unknown" size="small"
      sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem' }} />
  );
  const label = fmtFuzzer(type);
  const isDnstwist = type.startsWith('dnstwist-');
  const isKeyword  = type.startsWith('keyword-');
  const desc = getFuzzerDescription(type);
  return (
    <Tooltip title={desc} placement="top" arrow
      slotProps={{
        tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)',
                          p: 1, maxWidth: 280, fontSize: '0.72rem' } },
        arrow: { sx: { color: '#1a1430' } },
      }}>
      <Chip
        label={label} size="small"
        sx={{
          bgcolor: isDnstwist ? 'rgba(124,92,191,0.18)' : isKeyword ? 'rgba(251,146,60,0.15)' : 'rgba(248,113,113,0.15)',
          color:   isDnstwist ? '#a78bfa'              : isKeyword ? '#fb923c'               : '#f87171',
          fontSize: '0.68rem', cursor: 'default',
        }}
      />
    </Tooltip>
  );
}

function ClassificationChip({ label, reason }) {
  const meta = CLASSIFICATIONS.find((c) => c.value === label);
  if (!meta) {
    return <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>--</Typography>;
  }
  return (
    <Tooltip
      title={reason || 'Not yet classified: run a recheck'}
      placement="left" arrow
      slotProps={{
        tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)',
                          p: 1, maxWidth: 300, fontSize: '0.72rem' } },
        arrow: { sx: { color: '#1a1430' } },
      }}
    >
      <Chip
        label={meta.display} size="small"
        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '0.7rem', cursor: 'default' }}
      />
    </Tooltip>
  );
}

/* How the detected name relates to the monitored brand (from the scorer's
   match_class), plus the evidence-gate reason the final rank was floored/capped. */
const MATCH_CLASS_META = {
  'exact':          { display: 'EXACT',      bg: 'rgba(248,113,113,0.18)', color: '#f87171',
                      desc: 'Exact brand name on another TLD or as a subdomain label.' },
  'contained':      { display: 'CONTAINS',   bg: 'rgba(251,146,60,0.16)',  color: '#fb923c',
                      desc: 'Your brand name embedded in a longer domain (combosquat).' },
  'plausible-typo': { display: 'TYPO',       bg: 'rgba(124,92,191,0.20)',  color: '#a78bfa',
                      desc: 'A plausible keyboard or visual typo of the brand.' },
  'weak-neighbor':  { display: 'LOOK-ALIKE', bg: 'rgba(255,255,255,0.07)', color: 'rgba(232,228,248,0.6)',
                      desc: 'Resembles the brand but is likely a different name; on name alone it is kept low.' },
};

function parseMeta(breakdown) {
  if (!breakdown) return {};
  try {
    const b = typeof breakdown === 'string' ? JSON.parse(breakdown) : breakdown;
    return { match_class: b.match_class, tier: b.tier, tier_reason: b.tier_reason };
  } catch { return {}; }
}

function MatchClassChip({ breakdown }) {
  const { match_class, tier_reason } = parseMeta(breakdown);
  const meta = MATCH_CLASS_META[match_class];
  if (!meta) return null;
  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: '0.72rem' }}>
          {meta.desc}
          {tier_reason && (
            <Box sx={{ mt: 0.5, color: '#fbbf24' }}>Ranking: {tier_reason}</Box>
          )}
        </Box>
      }
      placement="top" arrow
      slotProps={{
        tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)',
                          p: 1, maxWidth: 300, fontSize: '0.72rem' } },
        arrow: { sx: { color: '#1a1430' } },
      }}>
      <Chip label={meta.display} size="small"
        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.4,
              bgcolor: meta.bg, color: meta.color, cursor: 'default' }} />
    </Tooltip>
  );
}

/* Match class + evidence-gate reason for the expanded detail panel. */
function MatchClassDetail({ breakdown }) {
  const { match_class, tier_reason } = parseMeta(breakdown);
  const meta = MATCH_CLASS_META[match_class];
  if (!meta) return null;
  return (
    <Box sx={{ mb: 1.2 }}>
      <Box display="flex" alignItems="center" gap={0.7} sx={{ mb: 0.4 }}>
        <Chip label={meta.display} size="small"
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.4,
                bgcolor: meta.bg, color: meta.color }} />
        <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.55)' }}>
          {meta.desc}
        </Typography>
      </Box>
      {tier_reason && (
        <Typography sx={{ fontSize: '0.66rem', color: '#fbbf24' }}>
          Why this rank: {tier_reason}
        </Typography>
      )}
    </Box>
  );
}

function ThreatIntelBadge({ flags }) {
  if (!flags) return null;
  try {
    const ti = typeof flags === 'string' ? JSON.parse(flags) : flags;
    if (!ti.is_flagged) return null;
    const sources = (ti.sources || []).join(', ');
    const details = (ti.details || []).join('\n');
    return (
      <Tooltip title={`Flagged by ${sources}${details ? ': ' + details : ''}`} placement="top" arrow
        slotProps={{ tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(248,113,113,0.3)', p: 1, fontSize: '0.72rem', maxWidth: 320 } }, arrow: { sx: { color: '#1a1430' } } }}>
        <Chip label="THREAT INTEL" size="small"
          sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800, letterSpacing: 0.5,
                bgcolor: 'rgba(248,113,113,0.2)', color: '#f87171', cursor: 'default',
                border: '1px solid rgba(248,113,113,0.3)' }} />
      </Tooltip>
    );
  } catch { return null; }
}

function CorroborationBadge({ channels }) {
  if (!channels) return null;
  try {
    const list = typeof channels === 'string' ? JSON.parse(channels) : channels;
    if (!Array.isArray(list) || list.length < 2) return null;
    return (
      <Tooltip title={`Seen independently by ${list.length} detection channels: ${list.join(', ')}`}
        placement="top" arrow
        slotProps={{ tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(45,212,191,0.3)', p: 1, fontSize: '0.72rem', maxWidth: 320 } }, arrow: { sx: { color: '#1a1430' } } }}>
        <Chip label={`${list.length}× CORROBORATED`} size="small"
          sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800, letterSpacing: 0.5,
                bgcolor: 'rgba(45,212,191,0.16)', color: '#2dd4bf', cursor: 'default',
                border: '1px solid rgba(45,212,191,0.3)' }} />
      </Tooltip>
    );
  } catch { return null; }
}

const tipSlotProps = {
  tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)', p: 1, fontSize: '0.72rem' } },
  arrow: { sx: { color: '#1a1430' } },
};

/* ── expanded row detail panel ───────────────────────────── */

function DetailItem({ label, value, mono }) {
  return (
    <Box display="flex" gap={1} sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.35)', width: 92, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography component="div" sx={{
        fontSize: '0.68rem', color: '#d6d0ee', wordBreak: 'break-all',
        fontFamily: mono ? 'monospace' : 'inherit', flex: 1,
      }}>
        {value ?? <span style={{ opacity: 0.3 }}>--</span>}
      </Typography>
    </Box>
  );
}

function DetailHeading({ children }) {
  return (
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.6,
                      textTransform: 'uppercase', color: 'rgba(232,228,248,0.32)', mb: 0.8 }}>
      {children}
    </Typography>
  );
}

// Registration dates are calendar days: show the UTC day, not a local time
const fmtRegDate = (v) => v ? parseUtc(v).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
}) : null;

const fmtDT = (v) => v ? parseUtc(v).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : null;

function AlertDetail({ alert, busyId, onStatus, onAllowlist, onOpenHelp }) {
  let ti = null;
  try { ti = alert.threat_intel_flags ? JSON.parse(alert.threat_intel_flags) : null; } catch { /* ignore */ }
  const evidence = [];
  if (alert.content_favicon_match) evidence.push(['Favicon', 'serves the brand site\'s exact favicon']);
  if (alert.content_layout_match) evidence.push(['Layout', 'page layout near-identical to the brand site (pHash)']);
  if (alert.content_title_match) evidence.push(['Title', 'page title near-identical to the brand site']);
  let kitMatches = [];
  try { kitMatches = alert.iok_matches ? JSON.parse(alert.iok_matches) : []; } catch { /* ignore */ }
  // ODbL attribution is required wherever IOK matches are shown. Only rules
  // tagged "kit" are impersonation evidence; the rest are informational.
  // Matches recorded while the page rendered the brand's own site are never
  // kit evidence (the scorer ignores them too); show them as indicators
  const kitRules = kitMatches.filter(m => (m.tags || []).includes('kit') && !m.own_site);
  const indicatorRules = kitMatches.filter(m => !(m.tags || []).includes('kit') || m.own_site);
  if (kitRules.length) {
    evidence.push(['Kit', `known phishing kit signature: ${kitRules.map(m => m.title || m.id).slice(0, 2).join('; ')} (IOK by phish.report, ODbL)`]);
  }
  if (indicatorRules.length) {
    evidence.push(['Indicators', `${indicatorRules.map(m => m.title || m.id).slice(0, 3).join(', ')} (informational, IOK by phish.report, ODbL)`]);
  }
  const busy = busyId === alert.id;

  return (
    <Box sx={{ px: 2.5, py: 2, bgcolor: 'rgba(0,0,0,0.18)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Risk factors */}
        <Box sx={{ flex: '1 1 200px', minWidth: 190, maxWidth: 300 }}>
          <DetailHeading>Risk factors</DetailHeading>
          <MatchClassDetail breakdown={alert.risk_breakdown} />
          <FactorBars breakdown={alert.risk_breakdown} />
        </Box>

        {/* Evidence */}
        <Box sx={{ flex: '1 1 230px', minWidth: 210, maxWidth: 340 }}>
          <DetailHeading>Evidence</DetailHeading>
          {evidence.length === 0 && !alert.urlscan_url && !ti?.is_flagged && (
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(232,228,248,0.25)' }}>
              No content or threat-intel evidence recorded
            </Typography>
          )}
          {evidence.map(([k, v]) => (
            <Box key={k} display="flex" alignItems="center" gap={0.7} sx={{ py: 0.25 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f87171', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#d6d0ee' }}>
                <b style={{ color: '#f87171' }}>{k}:</b> {v}
              </Typography>
            </Box>
          ))}
          {ti?.is_flagged && (
            <Box sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#f87171', fontWeight: 700 }}>
                Threat intel: {(ti.sources || []).join(', ')}
              </Typography>
              {(ti.details || []).slice(0, 3).map((d, i) => (
                <Typography key={i} sx={{ fontSize: '0.64rem', color: 'rgba(232,228,248,0.45)' }}>
                  {d}
                </Typography>
              ))}
            </Box>
          )}
          {alert.urlscan_url && (
            <Box sx={{ mt: 1 }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.45)' }}>
                  urlscan verdict:
                </Typography>
                <Typography sx={{
                  fontSize: '0.66rem', fontWeight: 700,
                  color: alert.urlscan_verdict === 'malicious' ? '#f87171'
                    : (alert.urlscan_verdict || '').startsWith('suspicious') ? '#fbbf24' : '#60a5fa',
                }}>
                  {alert.urlscan_verdict || 'pending'}
                </Typography>
                <Typography component="a" href={alert.urlscan_url} target="_blank" rel="noopener noreferrer"
                  sx={{ fontSize: '0.64rem', color: '#a78bfa', textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' } }}>
                  full report
                </Typography>
              </Box>
              {alert.urlscan_screenshot && (
                <Box component="a" href={alert.urlscan_url} target="_blank" rel="noopener noreferrer"
                  sx={{ display: 'block', width: '100%', maxWidth: 280, borderRadius: 1.5,
                        overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)',
                        '&:hover': { borderColor: 'rgba(167,139,250,0.5)' } }}>
                  <Box component="img" src={alert.urlscan_screenshot} alt={`Screenshot of ${alert.detected_domain}`}
                    loading="lazy"
                    sx={{ width: '100%', display: 'block', maxHeight: 160, objectFit: 'cover', objectPosition: 'top' }} />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Infrastructure + timeline */}
        <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
          <DetailHeading>Infrastructure</DetailHeading>
          <DetailItem label="Resolved IP" value={alert.resolved_ip} mono />
          <DetailItem label="Registrar" value={alert.registrar} />
          <DetailItem label="MX servers" value={alert.mx_servers ? alert.mx_servers.replace(/,/g, ', ') : (alert.has_mx ? 'yes' : null)} mono />
          <DetailItem label="SPF / DMARC" value={`${alert.has_spf ? 'SPF' : 'no SPF'} / ${alert.has_dmarc ? 'DMARC' : 'no DMARC'}`} />
          <DetailItem label="RDAP status" value={alert.rdap_status ? alert.rdap_status.split(',').map(s => getRdapStatusInfo(s).label).join(', ') : null} />
          <DetailItem label="Cert issuer" value={alert.certificate_issuer} />
          <Box sx={{ mt: 1.2 }}>
            <DetailHeading>Timeline</DetailHeading>
            <DetailItem label="Registered" value={fmtRegDate(alert.whois_created_date)} />
            <DetailItem label="Cert logged" value={fmtDT(alert.certificate_date)} />
            <DetailItem label="First seen" value={fmtDT(alert.detection_date)} />
            <DetailItem label="Last recheck" value={fmtDT(alert.last_recheck_date)} />
            {alert.previous_label && alert.previous_label !== alert.label && (
              <DetailItem label="Class change" value={`${alert.previous_label} to ${alert.label || 'unclassified'}`} />
            )}
            {alert.notes && <DetailItem label="Notes" value={alert.notes} />}
          </Box>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button size="small" startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
          disabled={busy || alert.status === 'reviewed'}
          onClick={() => onStatus(alert, 'reviewed')}
          sx={detailBtnSx('#4ade80')}>
          Mark reviewed
        </Button>
        <Button size="small" startIcon={<BlockOutlinedIcon sx={{ fontSize: 15 }} />}
          disabled={busy || alert.status === 'false_positive'}
          onClick={() => onStatus(alert, 'false_positive')}
          sx={detailBtnSx('#fbbf24')}>
          False positive
        </Button>
        <Button size="small" startIcon={<VerifiedUserOutlinedIcon sx={{ fontSize: 15 }} />}
          disabled={busy}
          onClick={() => onAllowlist(alert)}
          sx={detailBtnSx('#60a5fa')}>
          Allowlist domain
        </Button>
        {alert.status !== 'new' && (
          <Button size="small" disabled={busy}
            onClick={() => onStatus(alert, 'new')}
            sx={detailBtnSx('rgba(232,228,248,0.5)')}>
            Back to new
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {onOpenHelp && (
          <Typography onClick={() => onOpenHelp('detection-types')}
            sx={{ fontSize: '0.66rem', color: 'rgba(167,139,250,0.7)', cursor: 'pointer',
                  '&:hover': { color: '#a78bfa', textDecoration: 'underline' } }}>
            What do these fields mean?
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Triage buttons: mouse-precision sizing on desktop, ~44px thumb targets on
// touch (xs) so triaging from a phone doesn't take three attempts.
const detailBtnSx = (color) => ({
  fontSize: { xs: '0.78rem', sm: '0.68rem' }, textTransform: 'none', color,
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5,
  px: { xs: 1.6, sm: 1.2 }, py: { xs: 1, sm: 0.4 }, minHeight: { xs: 42, sm: 0 },
  '&:hover': { borderColor: color, bgcolor: 'rgba(255,255,255,0.03)' },
  '&.Mui-disabled': { color: 'rgba(232,228,248,0.2)', borderColor: 'rgba(255,255,255,0.05)' },
});

/* ── allowlist dialogs ───────────────────────────────────── */

// Common multi-label public suffixes: the registrable domain is one label
// ABOVE these, so "cover base" never collapses to a bare suffix like co.uk
// (which the backend also rejects, but the default checkbox must be safe).
const PUBLIC_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk', 'net.uk', 'sch.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'id.au',
  'co.nz', 'net.nz', 'org.nz', 'govt.nz', 'co.za', 'org.za', 'net.za',
  'co.jp', 'or.jp', 'ne.jp', 'ac.jp', 'com.cn', 'net.cn', 'org.cn',
  'co.in', 'net.in', 'org.in', 'co.kr', 'or.kr',
  'com.br', 'net.br', 'org.br', 'com.mx', 'com.ar', 'com.co',
  'com.sg', 'com.my', 'com.hk', 'com.tw', 'com.ph', 'co.id', 'co.th',
  'com.tr', 'com.ua', 'com.pl', 'com.es', 'com.pt', 'com.ng', 'co.il',
]);

function registrableDomain(domain) {
  const parts = domain.split('.');
  if (parts.length <= 2) return domain;
  const lastTwo = parts.slice(-2).join('.');
  const take = PUBLIC_SUFFIXES.has(lastTwo) ? 3 : 2;
  return parts.slice(-take).join('.');
}

function AllowlistConfirmDialog({ alert, open, onClose, onDone, showNotification }) {
  const domain = alert?.detected_domain || '';
  const baseDomain = registrableDomain(domain);
  const [coverBase, setCoverBase] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCoverBase(true); setNote(''); setBusy(false); }
  }, [open, domain]);

  if (!alert) return null;
  const target = (coverBase && baseDomain !== domain) ? baseDomain : domain;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await addAllowlistEntry(target, note || null, true);
      showNotification(
        `Allowlisted ${target}: ${data.suppressed} existing detection${data.suppressed !== 1 ? 's' : ''} suppressed. Future matches will be skipped.`,
        'success');
      onDone();
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error adding allowlist entry', 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose}
      PaperProps={{ sx: { bgcolor: '#1a1730', border: '1px solid rgba(255,255,255,0.1)',
                          backgroundImage: 'none', minWidth: { xs: 'auto', sm: 400 } } }}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e8e4f8', pb: 1 }}>
        Allowlist this domain?
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(232,228,248,0.6)', lineHeight: 1.6 }}>
          <b style={{ color: '#e8e4f8', fontFamily: 'monospace' }}>{target}</b> and all of its
          subdomains will never be saved as detections again, across scans, the NRD watcher,
          the CT stream, and urlscan hunts. Existing matching detections are marked false positive.
        </Typography>
        {baseDomain !== domain && (
          <FormControlLabel
            control={<Checkbox size="small" checked={coverBase}
              onChange={(e) => setCoverBase(e.target.checked)}
              sx={{ color: 'rgba(232,228,248,0.3)', '&.Mui-checked': { color: '#60a5fa' } }} />}
            label={<Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.55)' }}>
              Cover the whole registered domain ({baseDomain}), not just {domain}
            </Typography>}
            sx={{ mt: 1 }}
          />
        )}
        <TextField
          fullWidth size="small" placeholder="Note (optional): e.g. real unrelated company"
          value={note} onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { fontSize: '0.76rem' } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} disabled={busy}
          sx={{ color: 'rgba(232,228,248,0.5)' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={busy}
          sx={{ bgcolor: '#60a5fa', '&:hover': { bgcolor: '#3b82f6' } }}>
          {busy ? 'Adding...' : 'Allowlist'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AllowlistManagerDialog({ open, onClose, showNotification, onChanged }) {
  const [entries, setEntries] = useState(null);
  const [addDomain, setAddDomain] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await getAllowlist();
      setEntries(data);
    } catch {
      setEntries([]);
      showNotification('Error loading allowlist', 'error');
    }
  };

  useEffect(() => { if (open) { setEntries(null); load(); } }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!addDomain.trim()) return;
    setBusy(true);
    try {
      const { data } = await addAllowlistEntry(addDomain.trim(), null, true);
      showNotification(`Allowlisted ${data.entry.domain_name} (${data.suppressed} suppressed)`, 'success');
      setAddDomain('');
      await load();
      onChanged();
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error adding entry', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entry) => {
    try {
      await deleteAllowlistEntry(entry.id);
      showNotification(`Removed ${entry.domain_name} from the allowlist`, 'success');
      await load();
      onChanged();
    } catch {
      showNotification('Error removing entry', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { bgcolor: '#1a1730', border: '1px solid rgba(255,255,255,0.1)',
                          backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e8e4f8', pb: 1 }}>
        Allowlist
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.45)', lineHeight: 1.5, mb: 1.5 }}>
          Known-good domains that are never saved as detections (the entry covers its
          subdomains too). Removing an entry re-enables future detections but does not
          un-suppress old ones.
        </Typography>
        <Box display="flex" gap={1} mb={1.5}>
          <TextField fullWidth size="small" placeholder="example.com"
            value={addDomain} onChange={(e) => setAddDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem' } }} />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={busy || !addDomain.trim()}>
            Add
          </Button>
        </Box>
        {entries === null ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={18} sx={{ color: 'rgba(232,228,248,0.3)' }} />
          </Box>
        ) : entries.length === 0 ? (
          <Typography sx={{ fontSize: '0.74rem', color: 'rgba(232,228,248,0.25)', py: 1, textAlign: 'center' }}>
            No allowlist entries yet
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 260, overflowY: 'auto',
                     WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
            {entries.map(entry => (
              <Box key={entry.id} display="flex" alignItems="center" gap={1}
                sx={{ py: 0.7, px: 1, borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontFamily: 'monospace', color: '#e8e4f8' }}>
                    {entry.domain_name}
                  </Typography>
                  {entry.note && (
                    <Typography sx={{ fontSize: '0.64rem', color: 'rgba(232,228,248,0.35)' }}>
                      {entry.note}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => handleDelete(entry)}
                  aria-label={`Remove ${entry.domain_name}`}
                  sx={{ color: 'rgba(255,255,255,0.25)',
                        '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ color: 'rgba(232,228,248,0.5)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ── main component ──────────────────────────────────────── */

const PAGE_SIZES = [25, 50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

// Columns that can be hidden via the columns menu (domain + risk always show)
const OPTIONAL_COLS = ['date', 'indicators', 'fuzzer_type', 'label', 'rdap_status', 'registrar'];

function loadPref(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v != null ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}
function savePref(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

function AlertsTable({ refreshTrigger, showNotification, selectedScanId, onClearScan, onOpenHelp }) {
  const [alerts, setAlerts] = useState(null);   // null = first load in flight
  const [filterRisk, setFilterRisk] = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterRdap, setFilterRdap] = useState('');
  const [filterRegistrar, setFilterRegistrar] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('risk_score');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [density, setDensity] = useState(() => loadPref('dm_alerts_density', 'comfortable'));
  const [hiddenCols, setHiddenCols] = useState(() => new Set(loadPref('dm_alerts_hidden_cols', [])));
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const [allowTarget, setAllowTarget] = useState(null);      // alert to allowlist
  const [showAllowManager, setShowAllowManager] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  // Mobile: the filter dropdowns collapse behind one toggle so the list is
  // visible without scrolling past 3-4 wrapped rows of controls.
  const [showFilters, setShowFilters] = useState(false);

  // Stale-response guard: a slow earlier fetch must not overwrite a later one
  const alertsReqRef = useRef(0);
  const loadAlerts = async () => {
    const thisReq = ++alertsReqRef.current;
    try {
      const params = { limit: 1000 };
      if (selectedScanId != null) params.scan_id = selectedScanId;
      const { data } = await getAlerts(params);
      if (thisReq !== alertsReqRef.current) return;
      setAlerts(data);
      if (data.length >= 1000) {
        showNotification('Showing the first 1,000 findings. Narrow the filters or select a scan to see the rest.', 'warning');
      }
    } catch {
      if (thisReq !== alertsReqRef.current) return;
      setAlerts(prev => prev || []);   // leave any loaded data; never stick on skeletons
      showNotification('Error loading alerts', 'error');
    }
  };

  // A new scan scope drops the old rows first, so a failed load can never
  // leave unfiltered findings under the 'results for scan #N' banner
  const lastScopeRef = useRef(selectedScanId);
  useEffect(() => {
    if (lastScopeRef.current !== selectedScanId) {
      lastScopeRef.current = selectedScanId;
      setAlerts(null);
    }
    loadAlerts();
  }, [refreshTrigger, selectedScanId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loading = alerts === null;
  const loadedAlerts = useMemo(() => alerts || [], [alerts]);

  // Derived filter options
  const detectionTypes = useMemo(() => {
    const types = new Set(loadedAlerts.map(a => a.fuzzer_type).filter(Boolean));
    return [...types].sort();
  }, [loadedAlerts]);

  const monitoredDomains = useMemo(() => {
    const domains = new Set(loadedAlerts.map(a => a.monitored_domain_name).filter(Boolean));
    return [...domains].sort();
  }, [loadedAlerts]);

  const rdapStatuses = useMemo(() => {
    const statuses = new Set(loadedAlerts.map(a => a.rdap_status).filter(Boolean));
    return [...statuses].sort();
  }, [loadedAlerts]);

  const registrars = useMemo(() => {
    const regs = new Set(loadedAlerts.map(a => a.registrar).filter(Boolean));
    return [...regs].sort();
  }, [loadedAlerts]);

  // Auto-clear filter values that no longer exist in the current dataset's
  // dropdown options (e.g. after switching to a scan whose results don't
  // contain the previously-selected monitored_domain). Without this, the
  // user sees an empty table with a hidden filter still applied and no UI
  // cue explaining why.
  useEffect(() => {
    if (filterType && !detectionTypes.includes(filterType)) setFilterType('');
    if (filterDomain && !monitoredDomains.includes(filterDomain)) setFilterDomain('');
    if (filterRdap && !rdapStatuses.includes(filterRdap)) setFilterRdap('');
    if (filterRegistrar && !registrars.includes(filterRegistrar)) setFilterRegistrar('');
  }, [detectionTypes, monitoredDomains, rdapStatuses, registrars,
      filterType, filterDomain, filterRdap, filterRegistrar]);

  // Client-side filter + sort
  const sortedAlerts = useMemo(() => {
    let filtered = loadedAlerts;
    if (filterRisk) filtered = filtered.filter(a => a.risk_score >= Number(filterRisk));
    if (filterLabel === 'unclassified') filtered = filtered.filter(a => !a.label);
    else if (filterLabel) filtered = filtered.filter(a => a.label === filterLabel);
    if (filterType) filtered = filtered.filter(a => a.fuzzer_type === filterType);
    if (filterDomain) filtered = filtered.filter(a => a.monitored_domain_name === filterDomain);
    if (filterRdap) filtered = filtered.filter(a => a.rdap_status === filterRdap);
    if (filterRegistrar) filtered = filtered.filter(a => a.registrar === filterRegistrar);
    if (filterStatus) filtered = filtered.filter(a => (a.status || 'new') === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(a =>
        (a.detected_domain || '').toLowerCase().includes(q) ||
        (a.monitored_domain_name || '').toLowerCase().includes(q) ||
        (a.registrar || '').toLowerCase().includes(q) ||
        (a.fuzzer_type || '').toLowerCase().includes(q) ||
        (a.label || '').toLowerCase().includes(q) ||
        (a.classification_reason || '').toLowerCase().includes(q) ||
        (a.rdap_status || '').toLowerCase().includes(q)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'detected_domain':
          av = a.detected_domain.toLowerCase(); bv = b.detected_domain.toLowerCase(); break;
        case 'fuzzer_type':
          av = (a.fuzzer_type || '').toLowerCase(); bv = (b.fuzzer_type || '').toLowerCase(); break;
        case 'risk_score':
          av = a.risk_score; bv = b.risk_score; break;
        case 'label':
          av = (a.label || '').toLowerCase(); bv = (b.label || '').toLowerCase(); break;
        case 'rdap_status':
          av = (a.rdap_status || '').toLowerCase(); bv = (b.rdap_status || '').toLowerCase(); break;
        case 'registrar':
          av = (a.registrar || '').toLowerCase(); bv = (b.registrar || '').toLowerCase(); break;
        case 'date':
          av = a.detection_date ? parseUtc(a.detection_date).getTime() : 0;
          bv = b.detection_date ? parseUtc(b.detection_date).getTime() : 0; break;
        default: av = 0; bv = 0;
      }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [loadedAlerts, filterRisk, filterLabel, filterType, filterDomain, filterRdap, filterRegistrar, filterStatus, search, sortCol, sortDir]);

  // Reset to first page when filters, sort, or scan selection change
  useEffect(() => { setPage(0); }, [filterRisk, filterLabel, filterType, filterDomain, filterRdap, filterRegistrar, filterStatus, search, sortCol, sortDir, selectedScanId]);

  const totalPages = Math.max(1, Math.ceil(sortedAlerts.length / pageSize));
  // Clamp the page if the dataset shrank under us (e.g. a refetch after
  // allowlisting or a post-scan refresh returns fewer rows than the current
  // page starts at) — otherwise the body reads "no alerts" over a valid set.
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [totalPages, page]);
  const pagedAlerts = sortedAlerts.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'risk_score' ? 'desc' : 'asc');
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterRisk) params.min_risk_score = filterRisk;
      if (selectedScanId != null) params.scan_id = selectedScanId;
      if (filterLabel === 'unclassified') params.label = 'none';
      else if (filterLabel) params.label = filterLabel;
      if (filterType) params.fuzzer_type = filterType;
      if (filterDomain) params.monitored_domain = filterDomain;
      if (filterRdap) params.rdap_status = filterRdap;
      if (filterRegistrar) params.registrar = filterRegistrar;
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const { data } = await exportAlertsCsv(params);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain-findings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      // Revoking synchronously can cancel the download in Firefox and Safari
      setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 1000);
    } catch {
      showNotification('Error exporting CSV', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleSetStatus = async (alert, status) => {
    setBusyId(alert.id);
    try {
      await bulkAlertStatus([alert.id], status);
      setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status } : a));
      const verb = status === 'reviewed' ? 'marked reviewed'
        : status === 'false_positive' ? 'marked false positive' : 'moved back to new';
      showNotification(`${alert.detected_domain} ${verb}.`, 'success');
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error updating status', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const hasActiveFilters = filterRisk || filterLabel || filterType || filterDomain || filterRdap || filterRegistrar || filterStatus || search;

  const clearAllFilters = () => {
    setFilterRisk('');
    setFilterLabel('');
    setFilterType('');
    setFilterDomain('');
    setFilterRdap('');
    setFilterRegistrar('');
    setFilterStatus('');
    setSearch('');
    setPage(0);
  };

  const toggleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    savePref('dm_alerts_density', next);
  };

  const toggleCol = (id) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      savePref('dm_alerts_hidden_cols', [...next]);
      return next;
    });
  };

  const ALL_COLS = [
    { id: 'detected_domain', label: 'Detected Domain', minW: 220 },
    { id: 'date', label: 'Detected', minW: 108 },
    { id: 'indicators', label: 'Indicators', minW: 90, sortable: false },
    { id: 'fuzzer_type', label: 'Detection', minW: 100 },
    { id: 'risk_score', label: 'Risk', minW: 90 },
    { id: 'label', label: 'Class', minW: 90 },
    { id: 'rdap_status', label: 'RDAP Status', minW: 110 },
    { id: 'registrar', label: 'Registrar', minW: 130 },
  ];
  const COLS = ALL_COLS.filter(c => !hiddenCols.has(c.id));
  const totalColSpan = COLS.length + 2;   // + expand chevron + row actions
  const cellPy = density === 'compact' ? 0.6 : 1.5;

  const headSx = {
    fontWeight: 700, fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase', letterSpacing: 0.8, py: 1.2,
    background: '#16132a', borderBottom: '1px solid rgba(255,255,255,0.06)',
  };

  const iconBtnSx = {
    minWidth: { xs: 40, sm: 0 }, minHeight: { xs: 40, sm: 0 },
    px: 1, color: 'rgba(232,228,248,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
    '&.Mui-disabled': { color: 'rgba(232,228,248,0.15)', borderColor: 'rgba(255,255,255,0.05)' },
  };

  return (
    <Box>
      {/* ── SCAN FILTER BANNER ───────────────────────────── */}
      {selectedScanId != null && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 2, px: 1.5, py: 1, bgcolor: 'rgba(124,92,191,0.1)',
          border: '1px solid rgba(124,92,191,0.3)', borderRadius: 2,
        }}>
          <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600 }}>
            Showing results for scan #{selectedScanId}: {sortedAlerts.length} finding{sortedAlerts.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" onClick={onClearScan}
            sx={{ color: '#a78bfa', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            Show all
          </Typography>
        </Box>
      )}

      {/* ── ALERTS PANEL ─────────────────────────────────── */}
      <Box sx={{ bgcolor: '#13101f', borderRadius: 2, border: '1px solid rgba(255,255,255,0.07)',
                 overflow: 'hidden' }}>
        {/* Header + filters */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            {/* Title */}
            <Box display="flex" alignItems="center" gap={1}>
              <GppBadIcon sx={{ fontSize: 18, color: '#d97068', display: 'block', overflow: 'visible' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#e8e4f8' }}>
                Identified Domains
              </Typography>
              <Chip
                label={sortedAlerts.length}
                size="small"
                sx={{
                  bgcolor: sortedAlerts.length > 0 ? 'rgba(248,113,113,0.15)' : 'rgba(255,255,255,0.07)',
                  color: sortedAlerts.length > 0 ? '#f87171' : 'rgba(232,228,248,0.4)',
                  fontWeight: 700, height: 22, fontSize: '0.72rem',
                }}
              />
              {hasActiveFilters && (
                <Chip
                  label="Clear filters"
                  size="small"
                  onClick={clearAllFilters}
                  sx={{
                    height: 20, fontSize: '0.62rem', cursor: 'pointer',
                    bgcolor: 'rgba(167,139,250,0.12)', color: '#a78bfa',
                    '&:hover': { bgcolor: 'rgba(167,139,250,0.2)' },
                  }}
                />
              )}
            </Box>

            {/* Toolbar: density / columns / allowlist / export */}
            <Box display="flex" gap={1}>
              <Tooltip title={density === 'compact' ? 'Comfortable rows' : 'Compact rows'}>
                <Button size="small" onClick={toggleDensity} aria-label="Toggle row density"
                  sx={{ ...iconBtnSx, '&:hover': { color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' } }}>
                  {density === 'compact'
                    ? <DensityMediumIcon sx={{ fontSize: 16 }} />
                    : <DensitySmallIcon sx={{ fontSize: 16 }} />}
                </Button>
              </Tooltip>
              <Tooltip title="Show / hide columns">
                <Button size="small" onClick={(e) => setColMenuAnchor(e.currentTarget)} aria-label="Choose columns"
                  sx={{ ...iconBtnSx, '&:hover': { color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' } }}>
                  <ViewColumnOutlinedIcon sx={{ fontSize: 16 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Manage allowlist (known-good domains)">
                <Button size="small" onClick={() => setShowAllowManager(true)} aria-label="Manage allowlist"
                  sx={{ ...iconBtnSx, '&:hover': { color: '#60a5fa', borderColor: 'rgba(96,165,250,0.3)' } }}>
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />
                </Button>
              </Tooltip>
              <Tooltip title={exporting ? 'Exporting...' : 'Export as CSV'}>
                <span>
                  <Button size="small" onClick={handleExportCsv} disabled={exporting} aria-label="Export alerts as CSV"
                    sx={{ ...iconBtnSx, '&:hover': { color: '#4ade80', borderColor: 'rgba(74,222,128,0.3)' } }}>
                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Search + filter dropdowns */}
          <Box display="flex" gap={1.5} mt={1.5} flexWrap="wrap" alignItems="center">
            <TextField
              size="small" placeholder="Search..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: 'rgba(232,228,248,0.25)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 140,
                maxWidth: { xs: 'none', sm: 200 },
                flex: { xs: '1 1 auto', sm: '0 1 auto' },
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.78rem', color: '#e8e4f8',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(167,139,250,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#a78bfa' },
                },
                '& .MuiOutlinedInput-input': { py: 0.6, px: 0.5 },
              }}
            />
            {isMobile && (() => {
              const activeFilters = [filterRisk, filterStatus, filterLabel, filterDomain,
                                     filterType, filterRdap, filterRegistrar].filter(Boolean).length;
              return (
                <Button size="small" onClick={() => setShowFilters(v => !v)}
                  startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    minHeight: 38, px: 1.4, fontSize: '0.74rem', textTransform: 'none',
                    color: (showFilters || activeFilters) ? '#a78bfa' : 'rgba(232,228,248,0.5)',
                    border: '1px solid',
                    borderColor: (showFilters || activeFilters)
                      ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.1)',
                    borderRadius: 1.5, flexShrink: 0,
                  }}>
                  Filters{activeFilters ? ` (${activeFilters})` : ''}
                </Button>
              );
            })()}
            {(!isMobile || showFilters) && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
                       width: { xs: '100%', sm: 'auto' },
                       '& .MuiFormControl-root': { flex: { xs: '1 1 42%', sm: '0 0 auto' } } }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Min Risk</InputLabel>
              <Select value={filterRisk} label="Min Risk" onChange={(e) => setFilterRisk(e.target.value)}
                sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="90">Critical (90+)</MenuItem>
                <MenuItem value="70">High (70+)</MenuItem>
                <MenuItem value="40">Medium (40+)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 115 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="false_positive">False positive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Classification</InputLabel>
              <Select value={filterLabel} label="Classification" onChange={(e) => setFilterLabel(e.target.value)}
                sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unclassified">Unclassified</MenuItem>
                {CLASSIFICATIONS.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.display}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Monitored Domain</InputLabel>
              <Select value={filterDomain} label="Monitored Domain"
                onChange={(e) => setFilterDomain(e.target.value)} sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                {monitoredDomains.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Detection Type</InputLabel>
              <Select value={filterType} label="Detection Type"
                onChange={(e) => setFilterType(e.target.value)} sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                {detectionTypes.map(t => (
                  <MenuItem key={t} value={t}>{fmtFuzzer(t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>RDAP Status</InputLabel>
              <Select value={filterRdap} label="RDAP Status"
                onChange={(e) => setFilterRdap(e.target.value)} sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                {rdapStatuses.map(s => <MenuItem key={s} value={s}>{getRdapStatusInfo(s).label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.78rem' }}>Registrar</InputLabel>
              <Select value={filterRegistrar} label="Registrar"
                onChange={(e) => setFilterRegistrar(e.target.value)} sx={{ fontSize: '0.78rem' }}>
                <MenuItem value="">All</MenuItem>
                {registrars.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            </Box>
            )}
          </Box>
        </Box>

        {/* Columns menu */}
        <Menu anchorEl={colMenuAnchor} open={Boolean(colMenuAnchor)}
          onClose={() => setColMenuAnchor(null)}>
          {ALL_COLS.filter(c => OPTIONAL_COLS.includes(c.id)).map(c => (
            <MenuItem key={c.id} dense onClick={() => toggleCol(c.id)} sx={{ py: 0.3 }}>
              <Checkbox size="small" checked={!hiddenCols.has(c.id)}
                sx={{ py: 0.4, color: 'rgba(232,228,248,0.3)', '&.Mui-checked': { color: '#a78bfa' } }} />
              <ListItemText primary={c.label}
                primaryTypographyProps={{ fontSize: '0.78rem', color: '#e8e4f8' }} />
            </MenuItem>
          ))}
        </Menu>

        {/* Mobile: card list instead of a wide table. No inner scroll box —
            it flows into the page's single scroll container, otherwise a
            nested vh-height scroller strands the lower rows off-screen on
            touch browsers (the address bar makes 100vh taller than the
            visible viewport). */}
        {isMobile ? (
          <Box>
            {loading ? (
              <Box sx={{ p: 1.5 }}>
                {[0, 1, 2, 3].map(i => (
                  <Skeleton key={i} variant="rounded" height={64}
                    sx={{ bgcolor: 'rgba(255,255,255,0.04)', mb: 1, opacity: 1 - i * 0.18 }} />
                ))}
              </Box>
            ) : pagedAlerts.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <GppBadIcon sx={{ fontSize: 34, color: 'rgba(255,255,255,0.08)', mb: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {loadedAlerts.length === 0
                    ? 'No findings yet.'
                    : 'No alerts match the current filters.'}
                </Typography>
              </Box>
            ) : (
              pagedAlerts.map(alert => {
                const src = detectionSource(alert.fuzzer_type);
                const expanded = expandedId === alert.id;
                const status = alert.status || 'new';
                const sMeta = STATUS_META[status] || STATUS_META.new;
                const dim = status === 'false_positive';
                return (
                  <Box key={alert.id}
                    onClick={() => setExpandedId(expanded ? null : alert.id)}
                    sx={{
                      px: 1.5, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.05)',
                      bgcolor: expanded ? 'rgba(167,139,250,0.05)' : 'transparent',
                    }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={0.7} flexWrap="wrap">
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: src.color, flexShrink: 0 }} />
                          <Typography sx={{
                            fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700,
                            color: dim ? 'rgba(240,236,255,0.45)' : '#f0ecff',
                            textDecoration: dim ? 'line-through' : 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {alert.detected_domain}
                          </Typography>
                          {status !== 'new' && (
                            <Chip label={sMeta.label} size="small"
                              sx={{ height: 15, fontSize: '0.52rem', fontWeight: 800,
                                    bgcolor: sMeta.bg, color: sMeta.color,
                                    '& .MuiChip-label': { px: 0.7 } }} />
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.6} flexWrap="wrap">
                          <FuzzerChip type={alert.fuzzer_type} />
                          <MatchClassChip breakdown={alert.risk_breakdown} />
                          <ClassificationChip label={alert.label} reason={alert.classification_reason} />
                          {(alert.content_favicon_match || alert.content_layout_match || alert.content_title_match) && (
                            <Chip label="CLONE" size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: 'rgba(248,113,113,0.18)', color: '#f87171' }} />
                          )}
                          {alert.has_mx && (
                            <Chip label="MX" size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }} />
                          )}
                          <ThreatIntelBadge flags={alert.threat_intel_flags} />
                          <CorroborationBadge channels={alert.seen_channels} />
                        </Box>
                        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(232,228,248,0.3)', mt: 0.5 }}>
                          {alert.detection_date
                            ? parseUtc(alert.detection_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : ''}
                          {alert.registrar ? ` · ${alert.registrar}` : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ opacity: dim ? 0.5 : 1 }}>
                        <ScoreBadge score={alert.risk_score} />
                      </Box>
                    </Box>
                    {expanded && (
                      <Box onClick={(e) => e.stopPropagation()} sx={{ mx: -1.5, mt: 1 }}>
                        <AlertDetail
                          alert={alert}
                          busyId={busyId}
                          onStatus={handleSetStatus}
                          onAllowlist={setAllowTarget}
                          onOpenHelp={onOpenHelp}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        ) : (
        <TableContainer sx={{
          maxHeight: 'calc(100vh - 300px)', overflowX: 'auto',
          '&::-webkit-scrollbar': { width: 4, height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-corner': { bgcolor: 'transparent' },
        }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, width: 30, px: 0.5 }} />
                {COLS.map((col) => (
                  <TableCell key={col.id} sx={{ ...headSx, minWidth: col.minW }}
                    sortDirection={sortCol === col.id ? sortDir : false}>
                    {col.sortable === false ? col.label : (
                      <TableSortLabel
                        active={sortCol === col.id}
                        direction={sortCol === col.id ? sortDir : 'asc'}
                        onClick={() => handleSort(col.id)}
                        sx={{
                          color: 'inherit !important',
                          '& .MuiTableSortLabel-icon': { color: 'rgba(167,139,250,0.6) !important' },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ ...headSx, width: 96, px: 0.5 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [0, 1, 2, 3, 4].map(i => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell colSpan={totalColSpan} sx={{ py: 1.2 }}>
                      <Skeleton variant="rounded" height={26}
                        sx={{ bgcolor: 'rgba(255,255,255,0.04)', opacity: 1 - i * 0.15 }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : pagedAlerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColSpan} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                    <GppBadIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.08)', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                      {loadedAlerts.length === 0
                        ? 'No findings yet. The CT stream and NRD watcher run continuously; run a scan for a full sweep.'
                        : 'No alerts match the current filters.'}
                    </Typography>
                    {hasActiveFilters && loadedAlerts.length > 0 && (
                      <Typography variant="caption" onClick={clearAllFilters}
                        sx={{ color: '#a78bfa', cursor: 'pointer', mt: 0.5, display: 'block',
                              '&:hover': { textDecoration: 'underline' } }}>
                        Clear all filters
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                pagedAlerts.map((alert) => {
                  const src = detectionSource(alert.fuzzer_type);
                  const expanded = expandedId === alert.id;
                  const status = alert.status || 'new';
                  const sMeta = STATUS_META[status] || STATUS_META.new;
                  const dim = status === 'false_positive';
                  const cells = {
                    detected_domain: (
                      <TableCell key="detected_domain" sx={{ py: cellPy }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                          <Tooltip title={`Found via ${src.label}`} placement="top" arrow slotProps={tipSlotProps}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: src.color,
                                       flexShrink: 0, boxShadow: `0 0 6px ${src.color}66`, cursor: 'default' }} />
                          </Tooltip>
                          <Tooltip
                            title={alert.monitored_domain_name
                              ? `Monitored: ${alert.monitored_domain_name}`
                              : 'No monitored domain linked'}
                            placement="top" arrow slotProps={tipSlotProps}>
                            <Typography variant="body2" fontWeight={700}
                              sx={{ color: dim ? 'rgba(240,236,255,0.45)' : '#f0ecff',
                                    fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.4, cursor: 'default',
                                    textDecoration: dim ? 'line-through' : 'none',
                                    textDecorationColor: 'rgba(255,255,255,0.25)' }}>
                              {alert.detected_domain}
                            </Typography>
                          </Tooltip>
                          {status !== 'new' && (
                            <Chip label={sMeta.label} size="small"
                              sx={{ height: 15, fontSize: '0.52rem', fontWeight: 800,
                                    bgcolor: sMeta.bg, color: sMeta.color, letterSpacing: 0.4,
                                    '& .MuiChip-label': { px: 0.7 } }} />
                          )}
                        </Box>
                        {density === 'comfortable' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2, minHeight: 16 }}>
                            {alert.whois_created_date ? (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#a78bfa', lineHeight: 1 }}>
                                Reg {fmtRegDate(alert.whois_created_date)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.15)', lineHeight: 1 }}>
                                No reg date
                              </Typography>
                            )}
                            {alert.certificate_issuer && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1 }}>
                                · {(() => {
                                  const cnMatch = alert.certificate_issuer.match(/CN=([^,]+)/);
                                  return cnMatch ? cnMatch[1].trim() : alert.certificate_issuer.split(',')[0];
                                })()}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    ),
                    date: (
                      <TableCell key="date" sx={{ py: cellPy }}>
                        {alert.detection_date ? (
                          <Tooltip title={parseUtc(alert.detection_date).toLocaleString()} placement="top" arrow slotProps={tipSlotProps}>
                            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.55)', whiteSpace: 'nowrap', cursor: 'default' }}>
                              {parseUtc(alert.detection_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.15)' }}>--</Typography>
                        )}
                      </TableCell>
                    ),
                    indicators: (
                      <TableCell key="indicators" sx={{ py: cellPy }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <MatchClassChip breakdown={alert.risk_breakdown} />
                          {alert.has_mx && (
                            <Tooltip title={alert.mx_servers
                              ? `MX servers: ${alert.mx_servers.replace(/,/g, ', ')} (BEC risk)`
                              : 'Has MX records, can receive email (BEC/impersonation risk)'} placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="MX" size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', cursor: 'default' }} />
                            </Tooltip>
                          )}
                          {alert.has_spf && (
                            <Tooltip title="SPF record configured: domain can send authenticated email" placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="SPF" size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', cursor: 'default' }} />
                            </Tooltip>
                          )}
                          {alert.has_dmarc && (
                            <Tooltip title="DMARC policy configured: email sending infrastructure active" placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="DMARC" size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', cursor: 'default' }} />
                            </Tooltip>
                          )}
                          {alert.has_certificate && (
                            <Tooltip title={alert.certificate_issuer
                              ? `Certificate: ${alert.certificate_issuer}`
                              : 'Has SSL certificate'} placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="CERT" size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: 'rgba(167,139,250,0.12)', color: '#a78bfa', cursor: 'default' }} />
                            </Tooltip>
                          )}
                          {(alert.content_favicon_match || alert.content_layout_match || alert.content_title_match) && (
                            <Tooltip title={alert.content_favicon_match
                              ? 'Serves the brand site\'s exact favicon (impersonation evidence)'
                              : alert.content_layout_match
                              ? 'Page layout near-identical to the brand site (perceptual hash)'
                              : 'Page title near-identical to the brand site'} placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="CLONE" size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: 'rgba(248,113,113,0.18)', color: '#f87171', cursor: 'default' }} />
                            </Tooltip>
                          )}
                          {alert.urlscan_url && (
                            <Tooltip title={`urlscan verdict: ${alert.urlscan_verdict || 'pending'}. Click for the full report + screenshot (safe: no visit to the suspect site).`} placement="top" arrow slotProps={tipSlotProps}>
                              <Chip label="URLSCAN" size="small" component="a"
                                href={alert.urlscan_url} target="_blank" rel="noopener noreferrer" clickable
                                onClick={(e) => e.stopPropagation()}
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                      bgcolor: alert.urlscan_verdict === 'malicious' ? 'rgba(248,113,113,0.18)'
                                        : (alert.urlscan_verdict || '').startsWith('suspicious') ? 'rgba(251,191,36,0.15)'
                                        : 'rgba(96,165,250,0.15)',
                                      color: alert.urlscan_verdict === 'malicious' ? '#f87171'
                                        : (alert.urlscan_verdict || '').startsWith('suspicious') ? '#fbbf24'
                                        : '#60a5fa' }} />
                            </Tooltip>
                          )}
                          <ThreatIntelBadge flags={alert.threat_intel_flags} />
                          <CorroborationBadge channels={alert.seen_channels} />
                        </Box>
                      </TableCell>
                    ),
                    fuzzer_type: (
                      <TableCell key="fuzzer_type" sx={{ py: cellPy }}>
                        <FuzzerChip type={alert.fuzzer_type} />
                      </TableCell>
                    ),
                    risk_score: (
                      <TableCell key="risk_score" sx={{ py: cellPy }}>
                        <Tooltip
                          title={alert.risk_breakdown
                            ? <RiskBreakdown breakdown={alert.risk_breakdown} />
                            : 'No breakdown available'}
                          placement="left" arrow
                          slotProps={{
                            tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)', p: 1 } },
                            arrow: { sx: { color: '#1a1430' } },
                          }}
                        >
                          <Box sx={{ display: 'inline-block', opacity: dim ? 0.5 : 1 }}>
                            <ScoreBadge score={alert.risk_score} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ),
                    label: (
                      <TableCell key="label" sx={{ py: cellPy }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          {alert.previous_label && alert.previous_label !== alert.label && (
                            <Tooltip title={`Previously: ${alert.previous_label}`} placement="top" arrow slotProps={tipSlotProps}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.35)', fontFamily: 'monospace' }}>
                                  {alert.previous_label}
                                </Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(251,146,60,0.7)' }}>→</Typography>
                              </Box>
                            </Tooltip>
                          )}
                          <ClassificationChip label={alert.label} reason={alert.classification_reason} />
                        </Box>
                      </TableCell>
                    ),
                    rdap_status: (
                      <TableCell key="rdap_status" sx={{ py: cellPy }}>
                        {alert.rdap_status ? (() => {
                          const statuses = alert.rdap_status.split(',');
                          const isHold = statuses.some(s => /hold|pending|redemption/i.test(s));
                          const primaryInfo = getRdapStatusInfo(statuses[0]);
                          const tooltipLines = statuses.map(s => {
                            const info = getRdapStatusInfo(s);
                            return `${info.label}: ${info.desc}`;
                          });
                          return (
                            <Tooltip title={tooltipLines.join('\n')} placement="top" arrow
                              slotProps={{ tooltip: { sx: { ...tipSlotProps.tooltip.sx, whiteSpace: 'pre-line' } }, arrow: tipSlotProps.arrow }}>
                              <Chip label={primaryInfo.label} size="small"
                                sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, maxWidth: 140, cursor: 'default',
                                      bgcolor: isHold ? 'rgba(251,146,60,0.15)' : 'rgba(74,222,128,0.1)',
                                      color: isHold ? '#fb923c' : 'rgba(74,222,128,0.6)' }} />
                            </Tooltip>
                          );
                        })() : (
                          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(232,228,248,0.15)' }}>--</Typography>
                        )}
                      </TableCell>
                    ),
                    registrar: (
                      <TableCell key="registrar" sx={{ py: cellPy }}>
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.45)', whiteSpace: 'nowrap' }}>
                          {alert.registrar || '--'}
                        </Typography>
                      </TableCell>
                    ),
                  };

                  return (
                    <React.Fragment key={alert.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedId(expanded ? null : alert.id)}
                        sx={{
                          cursor: 'pointer',
                          '& > .MuiTableCell-root': expanded ? { borderBottom: 'none' } : {},
                          '&:hover .row-actions': { opacity: 1 },
                          bgcolor: expanded ? 'rgba(167,139,250,0.04) !important' : undefined,
                        }}
                      >
                        <TableCell sx={{ py: cellPy, px: 0.5, width: 30 }}>
                          <KeyboardArrowDownIcon sx={{
                            fontSize: 16, color: 'rgba(232,228,248,0.25)', display: 'block',
                            transform: expanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.18s ease',
                          }} />
                        </TableCell>
                        {COLS.map(c => cells[c.id])}
                        <TableCell sx={{ py: cellPy, px: 0.5, width: 96 }}>
                          <Box className="row-actions" onClick={(e) => e.stopPropagation()} sx={{
                            display: 'flex', gap: 0.2, opacity: { xs: 1, md: 0 },
                            transition: 'opacity 0.15s',
                          }}>
                            <Tooltip title="Mark reviewed" placement="top">
                              <span>
                                <IconButton size="small" disabled={busyId === alert.id || status === 'reviewed'}
                                  onClick={() => handleSetStatus(alert, 'reviewed')}
                                  sx={{ color: 'rgba(232,228,248,0.3)', p: 0.4,
                                        '&:hover': { color: '#4ade80', bgcolor: 'rgba(74,222,128,0.08)' } }}>
                                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Mark false positive" placement="top">
                              <span>
                                <IconButton size="small" disabled={busyId === alert.id || status === 'false_positive'}
                                  onClick={() => handleSetStatus(alert, 'false_positive')}
                                  sx={{ color: 'rgba(232,228,248,0.3)', p: 0.4,
                                        '&:hover': { color: '#fbbf24', bgcolor: 'rgba(251,191,36,0.08)' } }}>
                                  <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Allowlist domain (never alert again)" placement="top">
                              <span>
                                <IconButton size="small" disabled={busyId === alert.id}
                                  onClick={() => setAllowTarget(alert)}
                                  sx={{ color: 'rgba(232,228,248,0.3)', p: 0.4,
                                        '&:hover': { color: '#60a5fa', bgcolor: 'rgba(96,165,250,0.08)' } }}>
                                  <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={totalColSpan} sx={{ p: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <AlertDetail
                              alert={alert}
                              busyId={busyId}
                              onStatus={handleSetStatus}
                              onAllowlist={setAllowTarget}
                              onOpenHelp={onOpenHelp}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Footer with count + pagination */}
        {sortedAlerts.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
                     display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.25)' }}>
                {page * pageSize + 1}-{Math.min((page + 1) * pageSize, sortedAlerts.length)} of {sortedAlerts.length}
                {hasActiveFilters ? ` (filtered from ${loadedAlerts.length})` : ' findings'}
              </Typography>
              <Select
                value={pageSize}
                onChange={(e) => { setPageSize(e.target.value); setPage(0); }}
                size="small"
                sx={{
                  fontSize: '0.68rem', color: 'rgba(232,228,248,0.4)',
                  '& .MuiSelect-select': { py: 0.3, px: 1 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(167,139,250,0.3)' },
                  '& .MuiSvgIcon-root': { fontSize: 14, color: 'rgba(232,228,248,0.25)' },
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: '#1a1730', '& .MuiMenuItem-root': { fontSize: '0.72rem', color: '#c8c0e8' } } } }}
              >
                {PAGE_SIZES.map(s => (
                  <MenuItem key={s} value={s}>{s} / page</MenuItem>
                ))}
              </Select>
            </Box>
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" gap={1}>
                <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                  sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', color: 'rgba(232,228,248,0.5)',
                        '&:hover': { color: '#a78bfa' }, '&.Mui-disabled': { color: 'rgba(232,228,248,0.15)' } }}>
                  Prev
                </Button>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.35)' }}>
                  {page + 1} / {totalPages}
                </Typography>
                <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                  sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', color: 'rgba(232,228,248,0.5)',
                        '&:hover': { color: '#a78bfa' }, '&.Mui-disabled': { color: 'rgba(232,228,248,0.15)' } }}>
                  Next
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Allowlist dialogs */}
      <AllowlistConfirmDialog
        alert={allowTarget}
        open={Boolean(allowTarget)}
        onClose={() => setAllowTarget(null)}
        showNotification={showNotification}
        onDone={() => { setAllowTarget(null); loadAlerts(); }}
      />
      <AllowlistManagerDialog
        open={showAllowManager}
        onClose={() => setShowAllowManager(false)}
        showNotification={showNotification}
        onChanged={loadAlerts}
      />
    </Box>
  );
}

export default React.memo(AlertsTable);
